import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './DichVuYTe.css';
import './Home.css';
import logo from './logo.png';

// Icon components (same visuals as VN page)
const MedkitIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Medkit">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill={color} opacity=".15" />
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14,2 14,8 20,8" />
			<line x1="16" y1="13" x2="8" y2="13" />
			<line x1="16" y1="17" x2="8" y2="17" />
			<polyline points="10,9 9,9 8,9" />
		</g>
	</svg>
);

const ChatboxIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Chatbox">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill={color} opacity=".15" />
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
		</g>
	</svg>
);

const MedicalIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Medical">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill={color} opacity=".15" />
			<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
			<line x1="9" y1="9" x2="15" y2="9" />
			<line x1="9" y1="15" x2="15" y2="15" />
			<line x1="12" y1="9" x2="12" y2="15" />
		</g>
	</svg>
);

const DichVuYTeEN: React.FC = () => {
	const navigate = useNavigate();
	const [navOpen, setNavOpen] = React.useState(false);
	React.useEffect(() => {
		document.title = 'Healthcare services - Fconnect';
	}, []);

	return (
		<div className="home-page">
			{/* Header (reuse from Home) */}
			<header className="home-header">
				<div className="home-container header-inner">
					<div className="brand" onClick={() => { navigate('/en'); setNavOpen(false); }}>
						<img src={logo} alt="Fconnect" />
					</div>
					<button
						className="nav-toggle"
						aria-label={navOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={navOpen}
						aria-controls="main-navigation"
						onClick={() => setNavOpen((o) => !o)}
					>
						{navOpen ? '✕' : '☰'}
					</button>
					<nav id="main-navigation" className={`main-nav ${navOpen ? 'open' : ''}`}>
						<Link to="/en" onClick={() => setNavOpen(false)}>Home</Link>
						<Link to="/en/general-labor" onClick={() => setNavOpen(false)}>General labor</Link>
						<Link to="/en/agricultural-labor" onClick={() => setNavOpen(false)}>Agricultural labor</Link>
						<Link to="/en/healthcare-services" onClick={() => setNavOpen(false)}>Healthcare services</Link>
						<Link to="/en/privacy" onClick={() => setNavOpen(false)}>Privacy Policy</Link>
						<Link to="/en/terms" onClick={() => setNavOpen(false)}>Terms</Link>
						<Link to="/en/contact" onClick={() => setNavOpen(false)}>Contact</Link>
						<Link to="/" className="lang-link" onClick={() => setNavOpen(false)}>Tiếng Việt</Link>
					</nav>
				</div>
			</header>
			<div className={`nav-backdrop ${navOpen ? 'open' : ''}`} onClick={() => setNavOpen(false)} />

			{/* Banner Section */}
			<section className="ldt-banner-section">
				<div className="ldt-banner-content">
					<div className="ldt-banner-text">
						<h2>High-quality at-home healthcare services</h2>
						<p>Testing, consultation, and prescription fulfillment by professionals</p>
					</div>
					<div className="ldt-banner-visual">
						<div className="ldt-banner-icons">
							<MedkitIcon size={48} color="#F57C00" />
							<ChatboxIcon size={48} color="#F57C00" />
							<MedicalIcon size={48} color="#F57C00" />
						</div>
					</div>
				</div>
			</section>

			{/* Content */}
			<section className="home-container">
				<header className="ldt-hero">
					<h1>Healthcare services</h1>
					<p className="ldt-subtitle">Comprehensive healthcare with high-quality, safe, and convenient services.</p>
				</header>

				<div className="ldt-content">
					<section className="ldt-section">
						<h2 className="section-title">Services</h2>
						<div className="services-grid">
							<div className="service-card">
								<div className="service-icon"><MedkitIcon size={56} /></div>
								<h4>Testing</h4>
								<p>Blood tests, organ function, and screenings with accurate, quick results.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><ChatboxIcon size={56} /></div>
								<h4>Consultation</h4>
								<p>Specialist consultations with experienced doctors to address your questions.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><MedicalIcon size={56} /></div>
								<h4>Prescription fulfillment</h4>
								<p>Provide prescription medications with guaranteed quality and safe usage guidance.</p>
							</div>
						</div>
					</section>

					<section className="ldt-section">
						<h2 className="section-title">Testing packages</h2>
						<div className="packages-grid">
							<div className="package-card">
								<div className="package-header">
									<h3>Liver function</h3>
									<p className="package-sub">Evaluate liver health</p>
								</div>
								<ul className="package-list">
									<li>AST, ALT, GGT</li>
									<li>Total bilirubin</li>
									<li>Total protein</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Kidney and urinary function</h3>
									<p className="package-sub">Check kidney health</p>
								</div>
								<ul className="package-list">
									<li>Urea, Creatinine</li>
									<li>Uric acid</li>
									<li>Electrolytes</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Blood sugar</h3>
									<p className="package-sub">Monitor diabetes</p>
								</div>
								<ul className="package-list">
									<li>Fasting glucose</li>
									<li>HbA1c</li>
									<li>Insulin</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Cardiology - lipids</h3>
									<p className="package-sub">Assess cardiovascular risk</p>
								</div>
								<ul className="package-list">
									<li>Total cholesterol</li>
									<li>HDL, LDL, Triglycerides</li>
									<li>CRP, Homocysteine</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Cancer screening</h3>
									<p className="package-sub">Early detection</p>
								</div>
								<ul className="package-list">
									<li>CEA, AFP</li>
									<li>PSA (male)</li>
									<li>CA 125 (female)</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Parasites</h3>
									<p className="package-sub">Check for parasitic infections</p>
								</div>
								<ul className="package-list">
									<li>Fresh stool microscopy</li>
									<li>Blood tests</li>
									<li>Rapid tests</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Allergy</h3>
									<p className="package-sub">Identify allergy triggers</p>
								</div>
								<ul className="package-list">
									<li>Skin test</li>
									<li>Blood tests</li>
									<li>Allergy panels</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>DNA kinship</h3>
									<p className="package-sub">Establish family relationships</p>
								</div>
								<ul className="package-list">
									<li>Father - child</li>
									<li>Mother - child</li>
									<li>Siblings</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Blood tests</h3>
									<p className="package-sub">General health</p>
								</div>
								<ul className="package-list">
									<li>Complete blood count</li>
									<li>Blood type</li>
									<li>Basic coagulation</li>
								</ul>
							</div>
						</div>
					</section>

					<section className="ldt-section">
						<h2 className="section-title">Consultation packages</h2>
						<div className="packages-grid">
							<div className="package-card">
								<div className="package-header">
									<h3>Cardiology</h3>
									<p className="package-sub">Cardiology consultation</p>
								</div>
								<ul className="package-list">
									<li>Hypertension</li>
									<li>Coronary artery disease</li>
									<li>Arrhythmias</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>ENT</h3>
									<p className="package-sub">Ear, nose, throat consultation</p>
								</div>
								<ul className="package-list">
									<li>Rhinosinusitis</li>
									<li>Pharyngitis</li>
									<li>Otitis media</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Odonto-stomatology</h3>
									<p className="package-sub">Dental consultation</p>
								</div>
								<ul className="package-list">
									<li>Caries</li>
									<li>Gingivitis</li>
									<li>Orthodontics</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Neurology</h3>
									<p className="package-sub">Neurology consultation</p>
								</div>
								<ul className="package-list">
									<li>Headache</li>
									<li>Sleep disorders</li>
									<li>Neurological diseases</li>
								</ul>
							</div>
						</div>
					</section>
				</div>
			</section>

			{/* Footer (reuse from EN Home) */}
			<footer className="home-footer">
				<div className="home-container footer-inner">
					<div className="footer-contact">
						<p><strong>HIEP TIN PHAT FINANCE JOINT STOCK COMPANY</strong></p>
						<p><strong>Email:</strong> trile@fconnect.vn</p>
						<p><strong>Phone:</strong> 02513684266</p>
						<p><strong>Address:</strong> 11/4C, To 15, Kp 6, Nguyen Ai Quoc Street, Tran Bien Ward, Dong Nai City, Vietnam</p>
						<p><strong>Support hours:</strong> 8:00 - 22:00 (Mon - Sun)</p>
					</div>
					<div className="footer-stores">
						<a className="store-badge small appstore-badge" href="https://apps.apple.com/vn/app/fconnect-htp/id6752923786?l=vi" target="_blank" rel="noopener noreferrer">
							<span className="badge-icon" aria-hidden="true"></span>
							<span className="badge-text"><span className="badge-sub">Download on the</span><span className="badge-brand">App Store</span></span>
						</a>
						<a className="store-badge small play-badge" href="https://play.google.com/store/apps/details?id=com.taichinhhtp.fconnect&pcampaignid=web_share" target="_blank" rel="noopener noreferrer">
							<span className="badge-icon" aria-hidden="true">▶</span>
							<span className="badge-text"><span className="badge-sub">GET IT ON</span><span className="badge-brand">Google Play</span></span>
						</a>
					</div>
				</div>
				<div className="copyright">© 2025 Fconnect. All rights reserved.</div>
			</footer>
		</div>
	);
};

export default DichVuYTeEN;


